// Pestaña «Ingresos» de Finanzas (Mabel 7-oct-2026: «una aparte con todo lo que entra»). Es el apartado
// Ingresos del catálogo (Categoría › Concepto) con lo real del mes — la MISMA cuenta que Reportes ›
// Pronóstico y presupuesto › Por apartado (lib/letsmovePresupuesto.js `estructura`), para que no haya dos
// cifras. La lista de cobros uno por uno NO se repite aquí (se quitó el 23-sep por duplicada): vive en
// Caja › Ingresos y se enlaza.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Plus, TrendingUp } from 'lucide-react';
import { getPresupuesto, moneyEntero as money, Presupuesto } from '../api';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import { todayLocal, addMonthsClamped } from '../../../utils/dates';
import { useAuth } from '../../../context/AuthContext';
import IngresoForm from './IngresoForm';

export default function IngresosApartado() {
  const [mes, setMes] = useState(todayLocal().slice(0, 7));
  const [p, setP] = useState<Presupuesto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // «Registrar ingreso» (Mabel 8-oct-2026): renta del salón, eventos, otros — mismo permiso que «Registrar gasto».
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [showForm, setShowForm] = useState(false);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    let vivo = true;
    setLoading(true); setError('');
    getPresupuesto(mes).then((r) => { if (vivo) setP(r); })
      .catch((e) => { if (vivo) { setP(null); setError(e?.message || 'No se pudo cargar'); } })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [mes, recarga]);

  const ing = p?.estructura?.secciones.find((s) => s.section === 'ingreso') || null;
  const etiqueta = new Date(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)) - 1, 1)
    .toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })
    .replace(/^./, (c) => c.toUpperCase());   // «Octubre de 2026» (con `capitalize` salía «De»)
  const max = Math.max(1, ...(ing?.categorias || []).map((c) => c.total));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setMes((m) => addMonthsClamped(`${m}-01`, -1).slice(0, 7))}
            className="rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content-2 transition hover:bg-surface-2">←</button>
          <span className="min-w-[9rem] text-center text-sm font-medium text-content">{etiqueta}</span>
          <button onClick={() => setMes((m) => addMonthsClamped(`${m}-01`, 1).slice(0, 7))}
            className="rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content-2 transition hover:bg-surface-2">→</button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/caja?t=ingresos"
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
            Ver cada cobro en Caja › Ingresos <ArrowRight size={14} />
          </Link>
          {isAdmin && (
            <button onClick={() => setShowForm(true)} data-testid="registrar-ingreso"
              className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
              <Plus size={16} /> Registrar ingreso
            </button>
          )}
        </div>
      </div>
      {showForm && <IngresoForm onClose={() => setShowForm(false)}
        onCreated={(m) => { setShowForm(false); setMes(m); setRecarga((n) => n + 1); }} />}

      {loading ? (
        <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : !ing ? (
        <div className="rounded-2xl border border-line bg-surface">
          <EmptyState icon={TrendingUp} title="No se pudieron armar los ingresos del mes" subtitle={error || 'Intenta de nuevo en un momento.'} />
        </div>
      ) : (
        <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
          <div className="text-sm text-content-2">Total de ingresos · {etiqueta}</div>
          <div className="font-display text-3xl font-bold text-emerald-600 dark:text-emerald-400">{money(ing.total)}</div>
          <p className="text-xs text-content-3">Por categoría y concepto. Es la misma cifra de Reportes › Pronóstico y presupuesto.</p>

          <div className="mt-4 space-y-3">
            {ing.categorias.map((c) => (
              <div key={c.name}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="truncate font-medium text-content">{c.name}</span>
                  <span className="tabular-nums text-content">{money(c.total)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-surface-2">
                  <div className="h-full rounded-full bg-emerald-400/80" style={{ width: `${(Math.max(0, c.total) / max) * 100}%` }} />
                </div>
                {c.conceptos.length > 1 && (
                  <div className="mt-1 space-y-0.5 pl-3">
                    {c.conceptos.map((k) => (
                      <div key={k.name} className="flex items-center justify-between text-xs text-content-3">
                        <span className="truncate">{k.name}{k.n ? ` (${k.n})` : ''}</span><span className="tabular-nums">{money(k.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
